import type {
  Comment,
  Post,
  PostAuthor,
  PostType,
  ReportReason,
  ReportableContentType,
  User,
} from "../models";
import { daysFromToday } from "../mockDate";
import { mediaDescription } from "./mediaDescriptions";
import { ACTIVITIES, EVENTS, GALLERY, JOBS, LEARN, RIVALS, SCHOLARSHIPS } from "./seed";
import {
  CURRENT_USER_AUTHOR,
  MATCHED_USER_IDS,
  MOCK_AUTHORS,
  MOCK_CLUBS,
  SHARED_COURSES,
  USER_MAJORS,
  USER_ZODIACS,
  authorForId,
} from "./people";
import { seededBool, seededInt } from "./random";

/**
 * Port of `MockFeedService.swift`.
 *
 * The structure is deliberately preserved rather than tidied. One `store` keyed
 * by post id holds every typed post, seeded in a fixed order, then patched
 * across types afterwards (`flagRewardPosts`, `seedPreInteractions`). Those
 * cross-cutting patches are why the seed can't be split per post type — the
 * Hackathon event's prize, the featured window shared by two scholarships and
 * two jobs, and the pre-joined chess activity all reach across the tables.
 *
 * Mutations write back to `store`, so a like or an RSVP survives navigation for
 * the life of the session — which is exactly as long as a demo lasts.
 */

const delay = (seconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, seconds * 1000));

export class MockFeedService {
  private store = new Map<string, Post>();
  /** Gallery posts in seed order, for feed queries that ignore the typed posts. */
  private galleryPosts: Post[] = [];
  /** Course-tagged Learn items, in seed order; the posts themselves live in `store`. */
  private learnPostIds: string[] = [];

  constructor() {
    this.seedGalleryPosts();
    this.seedLearnPosts();
    this.seedEvents();
    this.seedActivities();
    this.seedRivals();
    this.seedScholarships();
    this.seedJobs();
    this.flagRewardPosts();
    this.seedPreInteractions();
  }

  /* ---------------- Seeding ---------------- */

  private seedGalleryPosts(): void {
    for (const [
      id,
      authorId,
      photo,
      content,
      hashtags,
      location,
      likeCount,
      commentCount,
      shareCount,
      viewCount,
      dayOffset,
      hour,
      minute,
    ] of GALLERY) {
      const post: Post = {
        id,
        content,
        mediaURL: `Characters/${photo}`,
        mediaType: "photo",
        mediaDescription: mediaDescription(photo),
        likeCount,
        commentCount,
        shareCount,
        viewCount,
        isLikedByMe: false,
        isSavedByMe: false,
        author: authorForId(authorId),
        hashtags,
        location: location ?? undefined,
        createdAt: daysFromToday(dayOffset, hour, minute),
        postType: "regular",
      };
      this.galleryPosts.push(post);
      this.store.set(id, post);
    }

    // Pre-seeded likes for demo flavour (top matches: Maya, Jamie).
    for (const id of ["p-maya-6", "p-maya-9", "p-jamie-6", "p-sophie-9"]) {
      const post = this.store.get(id);
      if (post) post.isLikedByMe = true;
    }
  }

  private seedLearnPosts(): void {
    for (const [
      id,
      authorId,
      photo,
      courseId,
      courseCode,
      title,
      content,
      hashtags,
      likeCount,
      commentCount,
      shareCount,
      viewCount,
      dayOffset,
      hour,
    ] of LEARN) {
      // No mediaDescription by design: these reuse gallery photos as lecture-note
      // media and some contradict the course they claim.
      const post: Post = {
        id,
        content,
        mediaURL: `Characters/${photo}`,
        mediaType: "photo",
        title,
        likeCount,
        commentCount,
        shareCount,
        viewCount,
        isLikedByMe: false,
        isSavedByMe: false,
        author: authorForId(authorId),
        hashtags,
        createdAt: daysFromToday(dayOffset, hour, 0),
        postType: "regular",
        courseId,
        courseCode,
      };
      this.store.set(id, post);
      this.learnPostIds.push(id);
    }
  }

  private seedEvents(): void {
    for (const [
      id,
      title,
      content,
      dayOffset,
      hour,
      endDayOffset,
      endHour,
      endMinute,
      location,
      isVirtual,
      attendees,
      isRSVPd,
      isInvited,
      invitedByIdx,
      authorIdx,
    ] of EVENTS) {
      const date = daysFromToday(dayOffset, hour, 0);
      const endDate = daysFromToday(endDayOffset, endHour, endMinute);
      this.store.set(id, {
        id,
        content,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/${id}/600/400`,
        title,
        likeCount: seededInt(`${id}-like`, 5, 200),
        commentCount: seededInt(`${id}-comment`, 0, 30),
        shareCount: seededInt(`${id}-share`, 0, 20),
        viewCount: seededInt(`${id}-view`, 50, 5000),
        isLikedByMe: false,
        isSavedByMe: false,
        author: MOCK_AUTHORS[authorIdx],
        hashtags: ["#event", "#utaustin"],
        location: location ?? undefined,
        createdAt: date,
        postType: "event",
        eventDate: date,
        eventEndDate: endDate,
        eventLocation: location ?? undefined,
        isVirtual,
        attendeeCount: attendees,
        isRSVPd,
        matchesAttending: isRSVPd
          ? [
              {
                id: "u1",
                displayName: "Jamie Park",
                profilePhotoURL: "Characters/jamie/jamie-profile",
                school: "UT Austin",
                major: "Computer Science",
                year: "2029",
              },
            ]
          : [],
        groupChatId: isRSVPd ? `gc-${id}` : undefined,
        isInvited: isInvited ? true : undefined,
        invitedBy: isInvited && invitedByIdx !== null ? MOCK_AUTHORS[invitedByIdx] : undefined,
      });
    }
  }

  private seedActivities(): void {
    for (const [
      id,
      title,
      content,
      category,
      dayOffset,
      hour,
      endDayOffset,
      endHour,
      endMinute,
      location,
      respondents,
      isJoined,
      authorIdx,
    ] of ACTIVITIES) {
      const date = dayOffset === null ? undefined : daysFromToday(dayOffset, hour, 0);
      const endDate =
        endDayOffset === null ? undefined : daysFromToday(endDayOffset, endHour, endMinute);
      this.store.set(id, {
        id,
        content,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/${id}/600/400`,
        title,
        likeCount: seededInt(`${id}-like`, 2, 50),
        commentCount: seededInt(`${id}-comment`, 0, 10),
        shareCount: seededInt(`${id}-share`, 0, 5),
        viewCount: seededInt(`${id}-view`, 20, 500),
        isLikedByMe: false,
        isSavedByMe: false,
        author: MOCK_AUTHORS[authorIdx],
        hashtags: ["#activity", `#${category}`],
        location: location ?? undefined,
        createdAt: date ?? daysFromToday(0, 9, 0),
        postType: "activity",
        eventDate: date,
        eventEndDate: endDate,
        eventLocation: location ?? undefined,
        category,
        isOpen: true,
        respondentCount: respondents,
        isJoinedByMe: isJoined,
      });
    }
  }

  private seedRivals(): void {
    for (const [
      id,
      title,
      content,
      category,
      dayOffset,
      hour,
      endDayOffset,
      endHour,
      endMinute,
      location,
      isOpen,
      acceptedByIdx,
      authorIdx,
    ] of RIVALS) {
      const date = dayOffset === null ? undefined : daysFromToday(dayOffset, hour, 0);
      const endDate =
        endDayOffset === null ? undefined : daysFromToday(endDayOffset, endHour, endMinute);
      this.store.set(id, {
        id,
        content,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/${id}/600/400`,
        title,
        likeCount: seededInt(`${id}-like`, 5, 100),
        commentCount: seededInt(`${id}-comment`, 0, 15),
        shareCount: seededInt(`${id}-share`, 0, 10),
        viewCount: seededInt(`${id}-view`, 30, 1000),
        isLikedByMe: false,
        isSavedByMe: false,
        author: MOCK_AUTHORS[authorIdx],
        hashtags: ["#rival", `#${category.replace(/ /g, "")}`],
        location: location ?? undefined,
        createdAt: date ?? daysFromToday(1, 16, 0),
        postType: "rival",
        eventDate: date,
        eventEndDate: endDate,
        eventLocation: location ?? undefined,
        category,
        isOpen,
        respondentCount: 0,
        acceptedBy: acceptedByIdx === null ? undefined : MOCK_AUTHORS[acceptedByIdx],
      });
    }
  }

  private seedScholarships(): void {
    for (const [
      id,
      title,
      content,
      amount,
      provider,
      eligibility,
      deadlineOffset,
      appURL,
      authorIdx,
    ] of SCHOLARSHIPS) {
      this.store.set(id, {
        id,
        content,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/${id}/600/400`,
        title,
        likeCount: seededInt(`${id}-like`, 10, 100),
        commentCount: seededInt(`${id}-comment`, 2, 20),
        shareCount: seededInt(`${id}-share`, 5, 40),
        viewCount: seededInt(`${id}-view`, 200, 2000),
        isLikedByMe: false,
        isSavedByMe: false,
        author: MOCK_AUTHORS[authorIdx],
        hashtags: ["#scholarship", "#utaustin"],
        createdAt: daysFromToday(-4, 10, 0),
        postType: "scholarship",
        hasReward: true,
        rewards: [{ label: "Award", value: `$${Math.trunc(amount)}` }],
        applicationURL: appURL,
        applicationDeadline: daysFromToday(deadlineOffset, 23, 59),
        scholarshipAmount: amount,
        scholarshipProvider: provider,
        eligibilityCriteria: eligibility,
      });
    }
  }

  private seedJobs(): void {
    for (const [
      id,
      title,
      content,
      employer,
      compensation,
      remote,
      location,
      deadlineOffset,
      appURL,
      authorIdx,
    ] of JOBS) {
      this.store.set(id, {
        id,
        content,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/${id}/600/400`,
        title,
        likeCount: seededInt(`${id}-like`, 5, 60),
        commentCount: seededInt(`${id}-comment`, 1, 15),
        shareCount: seededInt(`${id}-share`, 2, 20),
        viewCount: seededInt(`${id}-view`, 100, 1000),
        isLikedByMe: false,
        isSavedByMe: false,
        author: MOCK_AUTHORS[authorIdx],
        hashtags: ["#job", "#campusjob"],
        location: location ?? undefined,
        createdAt: daysFromToday(-3, 10, 0),
        postType: "job",
        hasReward: true,
        rewards: [{ label: "Pay", value: compensation }],
        applicationURL: appURL,
        applicationDeadline: daysFromToday(deadlineOffset, 23, 59),
        employerName: employer,
        compensationAmount: compensation,
        isRemote: remote,
      });
    }
  }

  /** Cross-cutting patches — the reason the seed tables can't be split apart. */
  private flagRewardPosts(): void {
    const hackathon = this.store.get("ev-11");
    if (hackathon) {
      hackathon.hasReward = true;
      hackathon.rewards = [{ label: "1st Place", value: "$5,000" }];
    }

    const featuredFrom = daysFromToday(-4, 0, 0);
    const featuredUntil = daysFromToday(27, 0, 0);

    for (const id of ["sch-1", "sch-2", "job-1", "job-2", "ev-11"]) {
      const post = this.store.get(id);
      if (post) {
        post.featuredFrom = featuredFrom;
        post.featuredUntil = featuredUntil;
      }
    }
  }

  private seedPreInteractions(): void {
    const studyAbroad = this.store.get("ev-4");
    if (studyAbroad) studyAbroad.isSavedByMe = true; // exploring

    const chess = this.store.get("act-7");
    if (chess) chess.isJoinedByMe = true; // casual interest
  }

  /* ---------------- Helpers ---------------- */

  private galleryPostsSorted(newestFirst = true): Post[] {
    return [...this.galleryPosts].sort((a, b) =>
      newestFirst ? b.createdAt.localeCompare(a.createdAt) : a.createdAt.localeCompare(b.createdAt),
    );
  }

  private galleryPostsSortedByViews(): Post[] {
    return [...this.galleryPosts].sort((a, b) => b.viewCount - a.viewCount);
  }

  private storedPosts(type: PostType): Post[] {
    return [...this.store.values()]
      .filter((p) => p.postType === type)
      .sort((a, b) => (a.eventDate ?? a.createdAt).localeCompare(b.eventDate ?? b.createdAt));
  }

  private page<T>(all: T[], page: number, limit: number): T[] {
    const start = page * limit;
    if (start >= all.length) return [];
    return all.slice(start, start + limit);
  }

  /** Generic `.regular` paging filler — never used for the Learn feed. */
  private makePosts(prefix: string, count: number, pageIndex: number): Post[] {
    return Array.from({ length: count }, (_, i) => {
      const idx = (pageIndex * count + i) % MOCK_AUTHORS.length;
      const id = `${prefix}-${pageIndex}-${i}`;
      return {
        id,
        content: `This is a mock ${prefix} post #${pageIndex * count + i + 1}`,
        mediaType: "video",
        thumbnailURL: `https://picsum.photos/seed/${prefix}${pageIndex}${i}/400/400`,
        likeCount: seededInt(`${id}-like`, 10, 5000),
        commentCount: seededInt(`${id}-comment`, 0, 200),
        shareCount: seededInt(`${id}-share`, 0, 100),
        viewCount: seededInt(`${id}-view`, 100, 50000),
        isLikedByMe: seededBool(`${id}-liked`),
        isSavedByMe: false,
        author: MOCK_AUTHORS[idx],
        hashtags: ["#college", "#freshman"],
        createdAt: daysFromToday(-(i + 1), 12, 0),
        postType: "regular" as const,
      };
    });
  }

  private makeClubPosts(clubId: string | null, pageIndex: number): Post[] {
    const clubPosts: [string, string, (typeof MOCK_CLUBS)[number]][] = [
      [
        "Just wrapped our weekly hackathon session! Amazing projects from everyone.",
        "ACM meetup was fire tonight 🔥",
        MOCK_CLUBS[0],
      ],
      [
        "Freshman Council is planning Spring Fest — volunteers needed!",
        "Who wants to help plan the biggest event of the semester?",
        MOCK_CLUBS[1],
      ],
      [
        "Intramural volleyball tryouts this Friday. All skill levels welcome!",
        "Come out and play! No experience needed.",
        MOCK_CLUBS[2],
      ],
      [
        "ACM study session for midterms — free pizza included",
        "Bring your hardest problems and we'll work through them together.",
        MOCK_CLUBS[0],
      ],
    ];
    const filtered =
      clubId === null ? clubPosts : clubPosts.filter(([, , club]) => club.id === clubId);

    return filtered.map(([content, title, club], i) => {
      const id = `clubpost-${clubId ?? "all"}-${pageIndex}-${i}`;
      return {
        id,
        content,
        mediaType: "photo",
        thumbnailURL: `https://picsum.photos/seed/club${pageIndex}${i}/600/400`,
        title,
        likeCount: seededInt(`${id}-like`, 5, 150),
        commentCount: seededInt(`${id}-comment`, 0, 20),
        shareCount: seededInt(`${id}-share`, 0, 10),
        viewCount: seededInt(`${id}-view`, 50, 2000),
        isLikedByMe: false,
        isSavedByMe: false,
        author: MOCK_AUTHORS[i % MOCK_AUTHORS.length],
        hashtags: ["#club", "#campuslife"],
        createdAt: daysFromToday(-(i + 1), 11, 0),
        postType: "regular" as const,
        club,
      };
    });
  }

  /* ---------------- Feeds ---------------- */

  async getForYouFeed(page: number, limit: number): Promise<Post[]> {
    await delay(0.5);
    const sorted = this.galleryPostsSorted().filter((p) => p.author.id !== "user-001");
    const posts = this.page(sorted, page, limit);
    if (posts.length === 0) return [];

    if (page === 0) {
      // Seed one of each typed post into the top of the feed so the card system
      // is visible without navigating anywhere.
      const [firstEvent] = this.storedPosts("event");
      const [firstActivity] = this.storedPosts("activity");
      const [firstRival] = this.storedPosts("rival");
      if (firstEvent) posts.splice(Math.min(1, posts.length), 0, firstEvent);
      if (firstActivity) posts.splice(Math.min(3, posts.length), 0, firstActivity);
      if (firstRival) posts.splice(Math.min(5, posts.length), 0, firstRival);
    }
    return posts;
  }

  /**
   * Course-scoped educational content. An empty `courseIds` means "no filter" —
   * every seeded Learn item is tagged, so callers that don't know the enrolment
   * still get real content rather than filler.
   */
  async getLearnFeed(courseIds: string[], page: number, limit: number): Promise<Post[]> {
    await delay(0.5);
    const all = this.learnPostIds
      .map((id) => this.store.get(id))
      .filter((p): p is Post => p !== undefined)
      .filter(
        (p) =>
          p.courseId !== undefined && (courseIds.length === 0 || courseIds.includes(p.courseId)),
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return this.page(all, page, limit);
  }

  async getExploreFeed(page: number, limit: number): Promise<Post[]> {
    await delay(0.5);
    const sorted = this.galleryPostsSortedByViews().filter((p) => p.author.id !== "user-001");
    return this.page(sorted, page, limit);
  }

  async getEarnFeed(page: number, limit: number): Promise<Post[]> {
    await delay(0.4);
    const all = [...this.store.values()]
      .filter((p) => p.postType === "scholarship" || p.postType === "job" || p.hasReward === true)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return this.page(all, page, limit);
  }

  async getPostsByType(type: PostType, page: number, limit: number): Promise<Post[]> {
    await delay(0.4);
    if (type === "regular") {
      return this.makePosts("regular", Math.min(limit, 10), page);
    }
    return this.page(this.storedPosts(type), page, limit);
  }

  async getClubPosts(clubId: string | null, page: number): Promise<Post[]> {
    await delay(0.4);
    return this.makeClubPosts(clubId, page);
  }

  async getMatchFeed(page: number, limit: number): Promise<Post[]> {
    await delay(0.5);
    const matchPosts = this.galleryPostsSorted().filter(
      (p) => MATCHED_USER_IDS.has(p.author.id) && p.author.id !== "user-001",
    );
    return this.page(matchPosts, page, limit).map((post) => ({
      ...post,
      isFromMatchedUser: true,
      sharedCourse: SHARED_COURSES[post.author.id],
    }));
  }

  async getPeerFeed(page: number, limit: number): Promise<Post[]> {
    await delay(0.5);
    const sorted = this.galleryPostsSorted().filter((p) => p.author.id !== "user-001");
    return this.page(sorted, page, limit).map((post) => ({
      ...post,
      isFromMatchedUser: true,
    }));
  }

  async getUserPosts(userId: string, page: number, limit: number): Promise<Post[]> {
    await delay(0.4);
    const userGallery = this.galleryPosts
      .filter((p) => p.author.id === userId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return this.page(userGallery, page, limit);
  }

  getPost(postId: string): Post | undefined {
    return this.store.get(postId);
  }

  /* ---------------- Mutations ---------------- */

  private mutate(postId: string, fn: (post: Post) => void): void {
    const post = this.store.get(postId);
    if (post) fn(post);
  }

  async likePost(postId: string): Promise<void> {
    await delay(0.2);
    this.mutate(postId, (p) => {
      p.isLikedByMe = true;
      p.likeCount += 1;
    });
  }

  async unlikePost(postId: string): Promise<void> {
    await delay(0.2);
    this.mutate(postId, (p) => {
      p.isLikedByMe = false;
      if (p.likeCount > 0) p.likeCount -= 1;
    });
  }

  async savePost(postId: string): Promise<void> {
    await delay(0.2);
    this.mutate(postId, (p) => {
      p.isSavedByMe = true;
    });
  }

  async unsavePost(postId: string): Promise<void> {
    await delay(0.2);
    this.mutate(postId, (p) => {
      p.isSavedByMe = false;
    });
  }

  async rsvpPost(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isRSVPd = true;
      if (p.attendeeCount !== undefined) p.attendeeCount += 1;
    });
  }

  async cancelRSVP(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isRSVPd = false;
      if (p.attendeeCount !== undefined && p.attendeeCount > 0) p.attendeeCount -= 1;
    });
  }

  async joinActivity(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isJoinedByMe = true;
      if (p.respondentCount !== undefined) p.respondentCount += 1;
    });
  }

  async leaveActivity(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isJoinedByMe = false;
      if (p.respondentCount !== undefined && p.respondentCount > 0) p.respondentCount -= 1;
    });
  }

  async acceptRival(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isOpen = false;
      p.acceptedBy = CURRENT_USER_AUTHOR;
    });
  }

  async cancelRival(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isOpen = true;
      p.acceptedBy = undefined;
    });
  }

  async applyPost(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isAppliedByMe = true;
    });
  }

  async cancelApplication(postId: string): Promise<void> {
    await delay(0.3);
    this.mutate(postId, (p) => {
      p.isAppliedByMe = false;
    });
  }

  async noteVideo(_videoId: string): Promise<void> {
    await delay(0.3);
  }

  async unnoteVideo(_videoId: string): Promise<void> {
    await delay(0.3);
  }

  /* ---------------- Comments ---------------- */

  async getComments(postId: string): Promise<Comment[]> {
    await delay(0.3);
    return Array.from({ length: 5 }, (_, i) => ({
      id: `comment-${postId}-${i}`,
      postId,
      text: `This is comment #${i + 1} on this post!`,
      likeCount: seededInt(`comment-${postId}-${i}`, 0, 50),
      isLikedByMe: false,
      author: MOCK_AUTHORS[i % MOCK_AUTHORS.length],
      createdAt: daysFromToday(-1, 10 + i, 0),
    }));
  }

  async addComment(postId: string, text: string): Promise<Comment> {
    await delay(0.3);
    return {
      id: `comment-new-${crypto.randomUUID()}`,
      postId,
      text,
      likeCount: 0,
      isLikedByMe: false,
      author: CURRENT_USER_AUTHOR,
      createdAt: daysFromToday(0, 12, 0),
    };
  }

  /* ---------------- Creation, search, users ---------------- */

  async createPost(input: {
    content: string;
    mediaURL?: string;
    mediaType?: string;
    title?: string;
    hashtags: string[];
    location?: string;
    competitionId?: string;
    postType: PostType;
    category?: string;
    clubId?: string;
  }): Promise<Post> {
    await delay(0.5);
    const post: Post = {
      id: `post-new-${crypto.randomUUID()}`,
      content: input.content,
      mediaURL: input.mediaURL,
      mediaType: input.mediaType,
      title: input.title,
      likeCount: 0,
      commentCount: 0,
      shareCount: 0,
      viewCount: 0,
      isLikedByMe: false,
      isSavedByMe: false,
      author: CURRENT_USER_AUTHOR,
      hashtags: input.hashtags,
      location: input.location,
      competitionId: input.competitionId,
      createdAt: daysFromToday(0, 12, 0),
      postType: input.postType,
      category: input.category,
      club: input.clubId ? MOCK_CLUBS.find((c) => c.id === input.clubId) : undefined,
    };
    if (input.postType !== "regular") this.store.set(post.id, post);
    return post;
  }

  async reportContent(
    _contentType: ReportableContentType,
    _contentId: string,
    _reason: ReportReason,
    _details?: string,
  ): Promise<void> {
    await delay(0.5);
  }

  async searchPosts(query: string, limit: number): Promise<Post[]> {
    await delay(0.3);
    const q = query.toLowerCase();
    const matching = this.galleryPosts.filter(
      (p) =>
        p.content.toLowerCase().includes(q) ||
        p.hashtags.some((h) => h.toLowerCase().includes(q)) ||
        p.author.displayName.toLowerCase().includes(q),
    );
    if (matching.length === 0) return this.galleryPostsSorted().slice(0, 5);
    return matching.slice(0, limit);
  }

  async searchUsers(query: string, limit: number): Promise<PostAuthor[]> {
    await delay(0.3);
    const q = query.toLowerCase();
    const matching = MOCK_AUTHORS.filter((a) => a.displayName.toLowerCase().includes(q));
    if (matching.length === 0) return MOCK_AUTHORS.slice(0, 5);
    return matching.slice(0, limit);
  }

  async getTrendingHashtags(): Promise<string[]> {
    return [
      "#freshman",
      "#collegelife",
      "#studygrind",
      "#campusvibes",
      "#dormlife",
      "#midterms",
      "#gameday",
      "#utaustin",
    ];
  }

  async getUser(userId: string): Promise<User> {
    await delay(0.3);
    const author = MOCK_AUTHORS.find((a) => a.id === userId);
    if (!author) {
      return {
        id: userId,
        primaryEmail: "user@utexas.edu",
        displayName: "Unknown User",
        isStudentVerified: false,
        froshPoints: 0,
        createdAt: daysFromToday(-180, 10, 0),
      };
    }
    return {
      id: author.id,
      primaryEmail: `${author.displayName.toLowerCase().replace(/ /g, ".")}@utexas.edu`,
      displayName: author.displayName,
      profilePhotoURL: author.profilePhotoURL,
      school: author.school,
      major: USER_MAJORS[userId] ?? "Undeclared",
      year: "2029",
      isStudentVerified: author.isVerified,
      froshPoints: seededInt(`${userId}-points`, 100, 3000),
      vibeAnalysisText: "Creative soul who loves campus life and connecting with others.",
      zodiacSign: USER_ZODIACS[userId] ?? "Aries",
      createdAt: daysFromToday(-180, 10, 0),
    };
  }

  async getSharedCourses(userId: string): Promise<string[]> {
    await delay(0.3);
    const code = SHARED_COURSES[userId];
    return code ? [code] : [];
  }
}
